import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Check, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { login } from "@/lib/api";
import { toast } from "sonner";

const PLANS = [
  {
    lookup: "mythos_monthly",
    name: "Monthly",
    price: "$9.99",
    cadence: "/month",
    features: ["Every extra included: Steamy, voice & clips", "All premium characters unlocked", "Unlimited text chat stays free for everyone", "Cancel anytime"],
    highlight: false,
  },
  {
    lookup: "mythos_quarterly",
    name: "Quarterly",
    price: "$24.99",
    cadence: "/quarter",
    features: ["Everything in Monthly", "Save 16% vs monthly", "Early access to contest picks", "Priority reader queue"],
    highlight: true,
    badge: "Most popular",
  },
  {
    lookup: "mythos_yearly",
    name: "Yearly",
    price: "$79.99",
    cadence: "/year",
    features: ["Everything in Quarterly", "Save 33% vs monthly", "500 bonus credits on signup", "Founding reader badge"],
    highlight: false,
    badge: "Best value",
  },
];

export default function Pricing() {
  const { user } = useAuth();
  const [busy, setBusy] = useState("");

  const subscribe = async (lookup_key) => {
    if (!user) { login(); return; }
    setBusy(lookup_key);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed");
      setBusy("");
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 md:py-20">
      <div className="text-center mb-14">
        <div className="font-mono text-[11px] uppercase tracking-[0.24em] text-amber-400 mb-2">Reader Plans</div>
        <h1 className="font-serif text-3xl md:text-5xl font-bold tracking-tight">Creators write free.<br/><span className="italic text-amber-400">Readers unlock everything.</span></h1>
        <p className="mt-4 text-slate-400 max-w-xl mx-auto">Every subscription funds the writers' contest pool. Cancel any time.</p>
      </div>
      <div className="grid md:grid-cols-3 gap-5 md:gap-6">
        {PLANS.map((p) => (
          <div
            key={p.lookup}
            data-testid={`plan-${p.lookup}`}
            className={`relative rounded-3xl p-8 border ${p.highlight ? "border-amber-500/50 bg-gradient-to-b from-amber-500/8 to-transparent" : "border-white/10 bg-[#0F1117]"}`}
          >
            {p.badge && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 contest-pill text-[9px]" style={{background: p.highlight ? "linear-gradient(90deg, #F59E0B, #F97316)" : undefined}}>
                {p.badge}
              </div>
            )}
            <div className="mb-6">
              <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-slate-500 mb-2">{p.name}</div>
              <div className="flex items-baseline gap-1">
                <span className="font-serif text-5xl font-bold">{p.price}</span>
                <span className="text-slate-500">{p.cadence}</span>
              </div>
            </div>
            <ul className="space-y-3 mb-8">
              {p.features.map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm text-slate-300">
                  <Check className="h-4 w-4 text-amber-400 mt-0.5 flex-shrink-0" /> {f}
                </li>
              ))}
            </ul>
            <Button
              data-testid={`subscribe-${p.lookup}`}
              onClick={() => subscribe(p.lookup)}
              disabled={busy === p.lookup}
              className={`w-full h-12 rounded-full font-semibold ${p.highlight ? "bg-amber-500 hover:bg-amber-400 text-black" : "bg-white/8 hover:bg-white/12 text-white"}`}
            >
              {busy === p.lookup ? "Redirecting…" : "Subscribe"}
            </Button>
          </div>
        ))}
      </div>
      <div className="mt-14 text-center text-xs text-slate-500 flex items-center justify-center gap-2">
        <Sparkles className="h-3 w-3 text-amber-400" />
        Powered by Stripe. Secure checkout.
      </div>
    </div>
  );
}
